"use client";

import { useQueryClient } from "@tanstack/react-query";
import { CACHE_TIERS } from "@workspace/types";
import { trpc } from "@/lib/trpc/react";

/**
 * Hook for fetching user festival status using tRPC
 * This enables proper cache invalidation when auto-marking occurs
 */
export function useUserFestivalStatusQuery(
	userId: string | undefined,
	festivalId: string | undefined,
) {
	const queryClient = useQueryClient();
	const utils = trpc.useUtils();

	const query = trpc.festival.getStatus.useQuery(
		{ festivalId: festivalId! },
		{
			enabled: Boolean(userId && festivalId),
			...CACHE_TIERS.REALTIME, // Always refetch for user-editable data
			gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
			retry: (failureCount, error) => {
				// Don't retry on auth errors
				if (error?.message?.includes("UNAUTHORIZED")) {
					return false;
				}
				// Retry other errors up to 2 times
				return failureCount < 2;
			},
		},
	);

	return {
		status: query.data?.status ?? null,
		isLoading: query.isLoading,
		error: query.error?.message ?? null,
		refetch: () => query.refetch(),
		invalidate: () =>
			utils.festival.getStatus.reset({ festivalId: festivalId! }),
	};
}
